import {
    Box,
    Button,
    Container,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GavelIcon from '@mui/icons-material/Gavel';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SecureDocuments from '../../assets/gif/SecureDocuments.gif';
import { getAllDocuments } from '../../data/api/document/DocumentRoutes';
import validErrors from '../../hooks/validErrors';
import DocumentContainer from './DocumentContainer';

const Documents = () => {
    const [documents, setDocuments] = useState();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const { data } = await getAllDocuments();
            setDocuments(data?.data);
        } catch (error) {
            validErrors(error);
        }
    };

    const infoCards = [
        {
            icon: <VerifiedUserIcon sx={{ fontSize: 30 }} />,
            title: 'Transparencia',
            description:
                'Ponemos a disposición documentos públicos e institucionales para fortalecer la confianza de la comunidad.',
        },
        {
            icon: <VisibilityIcon sx={{ fontSize: 30 }} />,
            title: 'Acceso fácil',
            description:
                'Consulta y descarga en pocos segundos la documentación oficial disponible para verificación.',
        },
        {
            icon: <GavelIcon sx={{ fontSize: 30 }} />,
            title: 'Legalidad',
            description:
                'Compartimos información que respalda formalmente la operación y representación legal de la entidad.',
        },
    ];

    return (
        <Box
            sx={{
                background: 'linear-gradient(180deg, #f8f8f8 0%, #ffffff 35%, #fafafa 100%)',
                minHeight: '100vh',
                py: { xs: 4, md: 7 },
            }}
        >
            <Container maxWidth="xl">
                {/* HERO */}
                <Paper
                    elevation={0}
                    sx={{
                        overflow: 'hidden',
                        borderRadius: { xs: '24px', md: '32px' },
                        background:
                            'linear-gradient(135deg, #f7fbff 0%, #ffffff 45%, #fff6f6 100%)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.05)',
                        mb: 6,
                    }}
                >
                    <Grid container spacing={0}>
                        <Grid
                            item
                            xs={12}
                            md={7}
                            sx={{
                                p: { xs: 3, md: 6 },
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 1.5,
                                        py: 0.8,
                                        borderRadius: '999px',
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        color: '#1976d2',
                                        mb: 2,
                                    }}
                                >
                                    <DescriptionIcon fontSize="small" />
                                    <Typography sx={{ fontWeight: 700, fontSize: '.92rem' }}>
                                        Documentación pública
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="h1"
                                    component="h1"
                                    sx={{
                                        fontSize: { xs: '2.5rem', md: '4.4rem' },
                                        lineHeight: 1,
                                        fontWeight: 700,
                                        color: '#1f1f1f',
                                        mb: 2,
                                    }}
                                >
                                    Nuestros documentos
                                </Typography>

                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.9,
                                        fontSize: { xs: '1rem', md: '1.08rem' },
                                        maxWidth: '760px',
                                        textAlign: 'justify',
                                        mb: 2,
                                    }}
                                >
                                    En esta sección encontrarás los documentos públicos que la
                                    entidad pone a disposición de la comunidad con el fin de
                                    demostrar su legalidad, transparencia y respaldo institucional.
                                    Queremos que cualquier persona pueda consultar esta información
                                    de forma clara, rápida y segura.
                                </Typography>

                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.9,
                                        fontSize: { xs: '1rem', md: '1.02rem' },
                                        maxWidth: '760px',
                                        textAlign: 'justify',
                                        mb: 3,
                                    }}
                                >
                                    También puedes revisar nuestros{' '}
                                    <Link
                                        to="/terms-conditions"
                                        style={{
                                            color: '#d32f2f',
                                            fontWeight: 700,
                                            textDecoration: 'none',
                                        }}
                                    >
                                        Términos y Condiciones
                                    </Link>{' '}
                                    para ampliar la información sobre nuestras políticas y
                                    lineamientos.
                                </Typography>

                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => {
                                        const section = document.getElementById('documents-list');
                                        if (section) {
                                            section.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}
                                    sx={{
                                        borderRadius: '999px',
                                        px: 3,
                                        py: 1.4,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        boxShadow: '0 10px 25px rgba(25,118,210,0.20)',
                                    }}
                                >
                                    Ver documentos
                                </Button>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={5}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                p: { xs: 3, md: 4 },
                                background:
                                    'radial-gradient(circle at center, rgba(25,118,210,0.08) 0%, rgba(255,255,255,0) 70%)',
                            }}
                        >
                            <GifDoc src={SecureDocuments} alt="Fenabocol Documents" />
                        </Grid>
                    </Grid>
                </Paper>

                {/* BLOQUES DE CONFIANZA */}
                <Grid container spacing={3} sx={{ mb: 6 }}>
                    {infoCards.map((item, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    p: 3,
                                    borderRadius: '24px',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    backgroundColor: '#fff',
                                    transition: 'all .25s ease',
                                    '&:hover': {
                                        transform: 'translateY(-5px)',
                                        boxShadow: '0 18px 36px rgba(0,0,0,0.08)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 58,
                                        height: 58,
                                        borderRadius: '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#1976d2',
                                        backgroundColor: 'rgba(25,118,210,0.10)',
                                        mb: 2,
                                    }}
                                >
                                    {item.icon}
                                </Box>

                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    {item.title}
                                </Typography>

                                <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                                    {item.description}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* LISTA DE DOCUMENTOS */}
                <Box id="documents-list">
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                mb: 1,
                                color: '#1f1f1f',
                                fontSize: { xs: '2.2rem', md: '3.4rem' },
                            }}
                        >
                            Documentos disponibles
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: '850px',
                                mx: 'auto',
                                color: 'text.secondary',
                                lineHeight: 1.8,
                            }}
                        >
                            Consulta, revisa y descarga la documentación oficial disponible
                            de la entidad.
                        </Typography>
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 },
                            borderRadius: '28px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            background: 'linear-gradient(180deg, #ffffff 0%, #fcfcfc 100%)',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
                        }}
                    >
                        {!documents ? (
                            <Grid container spacing={2}>
                                {[1, 2, 3, 4, 5, 6].map((item) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                                        <Stack spacing={1}>
                                            <Skeleton
                                                variant="rectangular"
                                                height={150}
                                                sx={{ borderRadius: '20px' }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                sx={{ fontSize: '1rem', width: '80%' }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                sx={{ fontSize: '.95rem', width: '60%' }}
                                            />
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <DocumentContainer documents={documents} />
                        )}
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};

export default Documents;

const GifDoc = styled.img`
    width: min(100%, 380px);
    display: block;
    pointer-events: none;
`;